import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, Table, Button, Input, Space, Tag, Dropdown, Modal, Form, Select, 
  DatePicker, TimePicker, InputNumber, Tooltip, message, Popconfirm, Tabs, 
  Typography, Divider, Badge, Switch, Progress, Radio, Checkbox, Alert
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, ExportOutlined, 
  ImportOutlined, SearchOutlined, FilterOutlined, EyeOutlined,
  UserAddOutlined, MoreOutlined, DownOutlined, CalendarOutlined,
  ClockCircleOutlined, TeamOutlined, DownloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import '../../styles/ExerciseManager.css';
import { 
  getExercises, 
  getCategories, 
  createExercise, 
  updateExercise, 
  deleteExercise, 
  assignExercise 
} from '../../api/exercises';
import { getTeacherStudents } from '../../api/teacherStudentRelations';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

const ExerciseManager = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exercises, setExercises] = useState([]);
  const [categories, setCategories] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');

  // 模态框状态
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [form] = Form.useForm();
  const [assignForm] = Form.useForm();

  // 新增批量操作相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [exportFormat, setExportFormat] = useState('excel');
  const [importFile, setImportFile] = useState(null);

  // 获取练习列表
  const fetchExercises = () => {
    setLoading(true);
    getExercises()
      .then(response => {
        setExercises(response.exercises);
        setLoading(false);
      })
      .catch(error => {
        console.error('获取练习列表失败:', error);
        message.error('获取练习列表失败，请稍后重试');
        setLoading(false);
      });
  };

  // 获取分类列表
  const fetchCategories = () => {
    getCategories()
      .then(categories => {
        setCategories(categories);
      })
      .catch(error => {
        console.error('获取分类列表失败:', error);
        message.error('获取分类列表失败，请稍后重试');
      });
  };

  // 获取学生列表
  const fetchStudents = () => {
    getTeacherStudents()
      .then(students => {
        setStudents(students);
      })
      .catch(error => {
        console.error('获取学生列表失败:', error);
        message.error('获取学生列表失败，请稍后重试');
      });
  };

  useEffect(() => {
    fetchExercises();
    fetchCategories();
    fetchStudents();
  }, []);

  // 筛选练习
  const filteredExercises = exercises.filter(ex => {
    return (
      (searchText === '' || ex.title.toLowerCase().includes(searchText.toLowerCase()) || ex.description.toLowerCase().includes(searchText.toLowerCase())) &&
      (filterStatus === 'all' || (filterStatus === 'active' && ex.is_active) || (filterStatus === 'inactive' && !ex.is_active)) &&
      (filterCategory === 'all' || ex.category_id === parseInt(filterCategory)) &&
      (filterDifficulty === 'all' || ex.difficulty === filterDifficulty)
    );
  });

  // 创建新练习
  const handleCreate = (values) => {
    console.log('创建练习:', values);
    
    setLoading(true);
    const exerciseData = {
      title: values.title,
      description: values.description,
      difficulty: values.difficulty,
      category_id: values.category,
      duration: values.duration,
      total_points: values.totalPoints,
      passing_points: values.passingPoints,
      is_active: values.isActive
    };
    
    createExercise(exerciseData)
      .then(newExercise => {
        // 更新本地状态
        const categoryName = categories.find(c => c.id === values.category)?.name || '';
        const exercise = {
          ...newExercise,
          category_name: categoryName
        };
        
        setExercises([exercise, ...exercises]);
        message.success('创建练习成功！');
        setIsCreateModalVisible(false);
        form.resetFields();
        setLoading(false);
      })
      .catch(error => {
        console.error('创建练习失败:', error);
        message.error('创建练习失败，请稍后重试');
        setLoading(false);
      });
  };

  // 编辑练习
  const handleEdit = (exercise) => {
    setCurrentExercise(exercise);
    form.setFieldsValue({
      title: exercise.title,
      description: exercise.description,
      category: exercise.category_id,
      difficulty: exercise.difficulty,
      duration: exercise.duration,
      totalPoints: exercise.total_points,
      passingPoints: exercise.passing_points,
      isActive: exercise.is_active
    });
    setIsCreateModalVisible(true);
  };

  // 保存编辑的练习
  const handleSave = (values) => {
    console.log('保存练习:', values);
    
    setLoading(true);
    const exerciseData = {
      title: values.title,
      description: values.description,
      difficulty: values.difficulty,
      category_id: values.category,
      duration: values.duration,
      total_points: values.totalPoints,
      passing_points: values.passingPoints,
      is_active: values.isActive
    };
    
    updateExercise(currentExercise.id, exerciseData)
      .then(updatedExercise => {
        // 更新本地状态
        const categoryName = categories.find(c => c.id === values.category)?.name || '';
        const updatedExercises = exercises.map(ex => {
          if (ex.id === currentExercise.id) {
            return {
              ...ex,
              ...updatedExercise,
              category_name: categoryName
            };
          }
          return ex;
        });
        
        setExercises(updatedExercises);
        message.success('更新练习成功！');
        setIsCreateModalVisible(false);
        setCurrentExercise(null);
        form.resetFields();
        setLoading(false);
      })
      .catch(error => {
        console.error('更新练习失败:', error);
        message.error('更新练习失败，请稍后重试');
        setLoading(false);
      });
  };

  // 删除练习
  const handleDelete = (id) => {
    console.log('删除练习:', id);
    
    setLoading(true);
    deleteExercise(id)
      .then(() => {
        const updatedExercises = exercises.filter(ex => ex.id !== id);
        setExercises(updatedExercises);
        message.success('删除练习成功！');
        setLoading(false);
      })
      .catch(error => {
        console.error('删除练习失败:', error);
        message.error('删除练习失败，请稍后重试');
        setLoading(false);
      });
  };

  // 分配练习
  const handleAssign = (exercise) => {
    navigate(`/teacher/assign-exercise?exerciseId=${exercise.id}`);
  };

  // 保存分配信息
  const handleSaveAssignment = (values) => {
    console.log('分配练习:', values);
    
    if (selectedStudents.length === 0) {
      message.warning('请至少选择一名学生');
      return;
    }
    
    setLoading(true);
    const timeRange = values.timeRange;
    const assignmentData = {
      studentIds: selectedStudents,
      startTime: timeRange[0].format('YYYY-MM-DD HH:mm:ss'),
      endTime: timeRange[1].format('YYYY-MM-DD HH:mm:ss'),
      note: values.note || ''
    };
    
    assignExercise(currentExercise.id, assignmentData)
      .then(result => {
        message.success(`成功将练习"${currentExercise.title}"分配给${selectedStudents.length}名学生！`);
        setIsAssignModalVisible(false);
        setCurrentExercise(null);
        setSelectedStudents([]);
        assignForm.resetFields();
        
        // 更新练习的分配数
        const updatedExercises = exercises.map(ex => {
          if (ex.id === currentExercise.id) {
            return {
              ...ex,
              assigned_count: ex.assigned_count + selectedStudents.length
            };
          }
          return ex;
        });
        
        setExercises(updatedExercises);
        setLoading(false);
      })
      .catch(error => {
        console.error('分配练习失败:', error);
        message.error('分配练习失败，请稍后重试');
        setLoading(false);
      });
  };

  // 创建管理页面
  const handleCreateNew = () => {
    setCurrentExercise(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      difficulty: 'medium',
      totalPoints: 100,
      passingPoints: 60,
      duration: 60
    });
    setIsCreateModalVisible(true);
  };

  // 查看练习详情
  const handleViewDetail = (exercise) => {
    // 跳转到练习详情页面
    navigate(`/teacher/create-exercise?id=${exercise.id}`);
  };

  // 导出练习数据
  const handleExport = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一条记录进行导出');
      return;
    }
    
    setExportModalVisible(true);
  };
  
  // 确认导出
  const confirmExport = () => {
    message.loading('正在导出数据...');
    
    // 模拟导出操作
    setTimeout(() => {
      message.success(`已成功导出 ${selectedRowKeys.length} 条练习数据为 ${exportFormat === 'excel' ? 'Excel' : 'CSV'} 格式`);
      setExportModalVisible(false);
      setSelectedRowKeys([]);
    }, 1500);
  };
  
  // 导入练习数据
  const handleImport = () => {
    setImportModalVisible(true);
  };
  
  // 确认导入
  const confirmImport = () => {
    if (!importFile) {
      message.warning('请先选择要导入的文件');
      return;
    }
    
    message.loading('正在导入数据...');
    
    // 模拟导入操作
    setTimeout(() => {
      message.success('数据导入成功');
      setImportModalVisible(false);
      setImportFile(null);
      
      // 刷新练习列表
      fetchExercises();
    }, 1500);
  };
  
  // 批量启用/停用练习
  const handleBatchStatusChange = (status) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一条记录');
      return;
    }
    
    const statusText = status ? '启用' : '停用';
    message.loading(`正在${statusText}选中的练习...`);
    
    // 模拟批量更新操作
    setTimeout(() => {
      const updatedExercises = exercises.map(ex => {
        if (selectedRowKeys.includes(ex.id)) {
          return {
            ...ex,
            is_active: status
          };
        }
        return ex;
      });
      
      setExercises(updatedExercises);
      message.success(`已成功${statusText} ${selectedRowKeys.length} 条练习`);
      setSelectedRowKeys([]);
    }, 1000);
  };
  
  // 批量删除练习
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一条记录');
      return;
    }
    
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 条练习记录吗？这将同时删除所有相关的分配记录和学生提交。`,
      okText: '确认',
      cancelText: '取消',
      onOk: () => {
        message.loading('正在删除...');
        
        // 模拟批量删除操作
        setTimeout(() => {
          const updatedExercises = exercises.filter(ex => !selectedRowKeys.includes(ex.id));
          setExercises(updatedExercises);
          message.success(`已成功删除 ${selectedRowKeys.length} 条练习`);
          setSelectedRowKeys([]);
        }, 1000);
      }
    });
  };
  
  // 表格行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: keys => setSelectedRowKeys(keys),
  };

  // 表格列定义
  const columns = [
    {
      title: '练习标题',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {!record.is_active && (
            <Tag color="orange" style={{ marginLeft: 8 }}>已停用</Tag>
          )}
        </div>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category_name',
      key: 'category',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '难度',
      dataIndex: 'difficulty',
      key: 'difficulty',
      render: (difficulty) => {
        const colorMap = {
          'easy': 'success',
          'medium': 'processing',
          'hard': 'warning',
          'advanced': 'error'
        };
        const textMap = {
          'easy': '简单',
          'medium': '中等',
          'hard': '困难',
          'advanced': '高级'
        };
        return <Badge status={colorMap[difficulty]} text={textMap[difficulty]} />;
      }
    },
    {
      title: '题目数',
      dataIndex: 'question_count',
      key: 'question_count',
      render: (count) => count
    },
    {
      title: '时长',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration) => `${duration}分钟`
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => handleViewDetail(record)}
          >
            查看
          </Button>
          <Button 
            icon={<UserAddOutlined />} 
            size="small"
            onClick={() => handleAssign(record)}
          >
            分配
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'edit',
                  icon: <EditOutlined />,
                  label: '编辑',
                  onClick: () => handleEdit(record)
                },
                {
                  key: 'delete',
                  icon: <DeleteOutlined />,
                  danger: true,
                  label: '删除',
                  onClick: () => Modal.confirm({
                    title: '确认删除',
                    content: `确定要删除练习"${record.title}"吗？这将同时删除所有相关的分配记录和学生提交。`,
                    okText: '确认',
                    cancelText: '取消',
                    onOk: () => handleDelete(record.id)
                  })
                }
              ]
            }}
          >
            <Button size="small">
              更多 <DownOutlined />
            </Button>
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div className="exercise-manager-container">
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={3}>练习管理</Title>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateNew}>
              创建练习
            </Button>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'import',
                    icon: <ImportOutlined />,
                    label: '导入练习',
                    onClick: handleImport
                  },
                  {
                    key: 'export',
                    icon: <ExportOutlined />,
                    label: '导出练习',
                    onClick: handleExport
                  },
                  {
                    type: 'divider',
                  },
                  {
                    key: 'activate',
                    label: '批量启用',
                    onClick: () => handleBatchStatusChange(true)
                  },
                  {
                    key: 'deactivate',
                    label: '批量停用',
                    onClick: () => handleBatchStatusChange(false)
                  },
                  {
                    key: 'delete',
                    icon: <DeleteOutlined />,
                    danger: true,
                    label: '批量删除',
                    onClick: handleBatchDelete
                  }
                ]
              }}
            >
              <Button>
                批量操作 <DownOutlined />
              </Button>
            </Dropdown>
          </Space>
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input.Search
              placeholder="搜索练习名称或描述"
              allowClear
              style={{ width: 300 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            
            <Select
              placeholder="状态筛选"
              style={{ width: 120 }}
              value={filterStatus}
              onChange={setFilterStatus}
            >
              <Option value="all">全部状态</Option>
              <Option value="active">已启用</Option>
              <Option value="inactive">已停用</Option>
            </Select>
            
            <Select
              placeholder="分类筛选"
              style={{ width: 160 }}
              value={filterCategory}
              onChange={setFilterCategory}
            >
              <Option value="all">全部分类</Option>
              {categories.map(cat => (
                <Option key={cat.id} value={cat.id.toString()}>{cat.name}</Option>
              ))}
            </Select>
            
            <Select
              placeholder="难度筛选"
              style={{ width: 120 }}
              value={filterDifficulty}
              onChange={setFilterDifficulty}
            >
              <Option value="all">全部难度</Option>
              <Option value="easy">简单</Option>
              <Option value="medium">中等</Option>
              <Option value="hard">困难</Option>
              <Option value="advanced">高级</Option>
            </Select>
          </Space>
        </div>
        
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredExercises}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50']
          }}
        />
      </Card>
      
      {/* 创建/编辑练习的模态框 */}
      <Modal
        title={currentExercise ? '编辑练习' : '创建新练习'}
        open={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={currentExercise ? handleSave : handleCreate}
          initialValues={{
            isActive: true,
            difficulty: 'medium',
            totalPoints: 100,
            passingPoints: 60,
            duration: 60
          }}
        >
          <Form.Item
            name="title"
            label="练习标题"
            rules={[{ required: true, message: '请输入练习标题' }]}
          >
            <Input placeholder="请输入练习标题" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="练习描述"
            rules={[{ required: true, message: '请输入练习描述' }]}
          >
            <Input.TextArea rows={4} placeholder="请输入练习描述，包括练习目标、适用学生等" />
          </Form.Item>
          
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="category"
              label="所属分类"
              rules={[{ required: true, message: '请选择所属分类' }]}
              style={{ flex: 1 }}
            >
              <Select placeholder="请选择分类">
                {categories.map(cat => (
                  <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                ))}
              </Select>
            </Form.Item>
            
            <Form.Item
              name="difficulty"
              label="难度级别"
              rules={[{ required: true, message: '请选择难度级别' }]}
              style={{ flex: 1 }}
            >
              <Select placeholder="请选择难度级别">
                <Option value="easy">简单</Option>
                <Option value="medium">中等</Option>
                <Option value="hard">困难</Option>
                <Option value="advanced">高级</Option>
              </Select>
            </Form.Item>
          </div>
          
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="duration"
              label="时长(分钟)"
              rules={[{ required: true, message: '请输入练习时长' }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={5} max={300} style={{ width: '100%' }} />
            </Form.Item>
            
            <Form.Item
              name="totalPoints"
              label="总分值"
              rules={[{ required: true, message: '请输入总分值' }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={10} max={1000} style={{ width: '100%' }} />
            </Form.Item>
            
            <Form.Item
              name="passingPoints"
              label="及格分值"
              rules={[
                { required: true, message: '请输入及格分值' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('totalPoints') >= value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('及格分不能高于总分'));
                  },
                }),
              ]}
              style={{ flex: 1 }}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          
          <Form.Item
            name="isActive"
            label="状态"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
          
          <Divider />
          
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsCreateModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit">
                {currentExercise ? '保存更改' : '创建练习'}
              </Button>
              {currentExercise && (
                <Button type="primary" onClick={() => handleViewDetail(currentExercise)}>
                  添加题目
                </Button>
              )}
            </Space>
          </div>
        </Form>
      </Modal>
      
      {/* 分配练习的模态框 */}
      <Modal
        title="分配练习"
        open={isAssignModalVisible}
        onCancel={() => setIsAssignModalVisible(false)}
        footer={null}
        width={700}
      >
        {currentExercise && (
          <Form
            form={assignForm}
            layout="vertical"
            onFinish={handleSaveAssignment}
          >
            <Card style={{ marginBottom: 16 }} size="small">
              <Text strong>练习信息：</Text>
              <Paragraph style={{ marginBottom: 0 }}>
                {currentExercise.title} 
                <Tag color="blue" style={{ marginLeft: 8 }}>{currentExercise.category_name}</Tag>
                <Tag color="orange" style={{ marginLeft: 8 }}>
                  {currentExercise.duration}分钟
                </Tag>
              </Paragraph>
            </Card>
            
            <Form.Item
              name="students"
              label="选择学生"
              rules={[{ required: true, message: '请选择至少一名学生' }]}
            >
              <Select
                mode="multiple"
                placeholder="请选择学生"
                optionFilterProp="label"
                style={{ width: '100%' }}
                onChange={setSelectedStudents}
                options={students.map(student => ({
                  value: student.id,
                  label: `${student.name} (${student.username}) - ${student.grade_level}`
                }))}
              />
            </Form.Item>
            
            <Form.Item
              name="timeRange"
              label="开始和结束时间"
              rules={[{ required: true, message: '请选择开始和结束时间' }]}
            >
              <RangePicker 
                showTime={{ format: 'HH:mm' }} 
                format="YYYY-MM-DD HH:mm" 
                style={{ width: '100%' }}
                placeholder={['开始时间', '结束时间']}
              />
            </Form.Item>
            
            <Form.Item
              name="note"
              label="备注"
            >
              <Input.TextArea rows={3} placeholder="可选：添加说明或要求" />
            </Form.Item>
            
            <Divider />
            
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setIsAssignModalVisible(false)}>取消</Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  disabled={selectedStudents.length === 0}
                >
                  确认分配
                </Button>
              </Space>
            </div>
          </Form>
        )}
      </Modal>
      
      {/* 导出数据模态框 */}
      <Modal
        title="导出练习数据"
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        onOk={confirmExport}
        okText="确认导出"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <Text>已选择 {selectedRowKeys.length} 条记录进行导出</Text>
        </div>
        
        <Form layout="vertical">
          <Form.Item label="导出格式">
            <Radio.Group value={exportFormat} onChange={e => setExportFormat(e.target.value)}>
              <Radio value="excel">Excel (.xlsx)</Radio>
              <Radio value="csv">CSV (.csv)</Radio>
            </Radio.Group>
          </Form.Item>
          
          <Form.Item label="导出字段">
            <Checkbox.Group 
              options={[
                { label: '基本信息', value: 'basic', disabled: true, defaultChecked: true },
                { label: '题目信息', value: 'questions' },
                { label: '分配情况', value: 'assignments' },
                { label: '统计数据', value: 'stats' }
              ]}
              defaultValue={['basic']}
            />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 导入数据模态框 */}
      <Modal
        title="导入练习数据"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setImportFile(null);
        }}
        onOk={confirmImport}
        okText="确认导入"
        cancelText="取消"
      >
        <Alert
          message="注意事项"
          description="请确保导入的Excel或CSV文件格式与模板一致，导入的数据将新增到系统中，不会覆盖现有数据。"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Form layout="vertical">
          <Form.Item label="选择文件">
            <Input.Group compact>
              <Input
                style={{ width: 'calc(100% - 120px)' }}
                value={importFile?.name || ''}
                readOnly
                placeholder="请选择Excel或CSV文件"
              />
              <Button style={{ width: 120 }}>
                选择文件
              </Button>
            </Input.Group>
          </Form.Item>
          
          <div style={{ marginTop: 16 }}>
            <Button type="link" icon={<DownloadOutlined />}>
              下载导入模板
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ExerciseManager;  
